/* =========================================================================
 * MOVE THIS CARD — tap-to-change pursuit status, built for a 390px phone.
 * [Jarvis · Claude] 2026-10-26
 *
 * Tap the status button → the stages open as full-width 44px rows → tap one →
 * "Moving…" → the card changes column ONLY when the server echoes the field
 * back as written (lib/pursuitMove.js#moveOutcome). A refusal or a dropped
 * connection says what happened and leaves the card where it was.
 *
 * Drag was not used: a sideways-scrolling kanban at 390px turns every drag
 * into a scroll, so a tap is the only move that works on Brian's phone.
 *
 * Every inner control stops propagation, because the whole card is the
 * "open the record" target — a tap on the status must not also open the drawer.
 * ========================================================================= */
import { useRef } from 'react'
import { useFencedState as useState } from '../../../lib/bowlMutationContext.jsx'
import { MOVE_STAGES, currentStage } from '../../../lib/pursuitMove.js'
import { pursuitMeta } from '../../../lib/championships.js'
import styles from './boardControls.module.css'

const stop = (e) => e.stopPropagation()

export default function PursuitMove({ c, onMove }) {
  const [open, setOpen] = useState(false)
  const [state, setState] = useState({ busy: '', error: '', done: '' })
  const pending = useRef(false)
  const uncertain = useRef(false)
  const stage = currentStage(c)
  const pm = pursuitMeta(c.pursuit)

  if (!onMove) {
    // Read-only space or no write grant: say so in words, not a dead button.
    return (
      <div className={styles.statusRead} onClick={stop} role="note">
        <span className={styles.statusDot} style={{ background: pm.color }} aria-hidden="true" />
        Status: {stage}
      </div>
    )
  }

  const move = async (to) => {
    if (to === stage || pending.current || uncertain.current) return
    pending.current = true
    setState({ busy: to, error: '', done: '' })
    const r = await onMove(c, to)
    pending.current = false
    if (r?.stale) return
    uncertain.current = r?.uncertain === true
    if (r?.landed) {
      setState({ busy: '', error: '', done: `Moved to ${to}${r.ledgerMissing ? ' (history row missing — see server log)' : ''}.` })
      setOpen(false)
    } else {
      setState({ busy: '', error: r?.reason || 'The move was not confirmed. Refresh and inspect its history before another attempt.', done: '' })
    }
  }

  return (
    <div className={styles.moveWrap} onClick={stop} onKeyDown={stop}>
      <button type="button" className={styles.statusBtn} aria-expanded={open}
        aria-label={`Status: ${stage}. Tap to move this card.`}
        onClick={() => { setOpen((o) => !o); setState((s) => ({ ...s, error: uncertain.current ? s.error : '', done: '' })) }}>
        <span className={styles.statusDot} style={{ background: pm.color }} aria-hidden="true" />
        <span className={styles.statusText}>{stage}</span>
        <span className={styles.statusCta}>{open ? 'Close' : 'Move'}</span>
      </button>

      {open && (
        <div className={styles.moveSheet} role="group" aria-label="Move this card to">
          {MOVE_STAGES.map((s) => {
            const here = s.key === stage
            const busy = state.busy === s.key
            return (
              <button key={s.key} type="button" disabled={here || !!state.busy || uncertain.current}
                className={`${styles.moveOpt} ${here ? styles.moveOptHere : ''}`}
                onClick={() => move(s.key)}>
                <span className={styles.statusDot} style={{ background: pursuitMeta(s.key).color }} aria-hidden="true" />
                {s.label}
                <span className={styles.moveOptNote}>{here ? 'here now' : busy ? 'Moving…' : ''}</span>
              </button>
            )
          })}
          <p className={styles.moveHint}>The move is saved to the record and to the permanent history, with your name.</p>
        </div>
      )}
      {state.error && <p className={styles.moveError} role="alert">{state.error}</p>}
      {state.done && <p className={styles.moveDone} role="status">{state.done}</p>}
    </div>
  )
}
